import Box from '@mui/material/Box';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';

import { FormSection } from 'common/components/FormSection';
import {
    DateTimePickerField,
    SwitchField,
    TextField,
} from 'common/forms/Field';

interface IProps {
    isMeeting?: boolean;
    isWebinarOrMeeting?: boolean;
    showGroupFilters?: boolean;
    showMoreSettings?: boolean;
}

export const SubscribeSettingsSection = ({
    isMeeting,
    isWebinarOrMeeting,
    showGroupFilters,
    showMoreSettings,
}: IProps) => {
    const [translate] = useTranslation();

    const { control, setValue } = useFormContext();
    const { errors } = useFormState();

    const publicParticipantList = useWatch({
        control,
        name: 'publicParticipantList',
    });
    const showParticipantList = useWatch({
        control,
        name: 'showParticipantList',
    });

    return (
        <FormSection
            renderExpanded
            description={
                <Box>
                    {translate('offer.offerSubscribeSettingsDescription')}
                </Box>
            }
            hasError={!!errors?.trainerApproval}
            sx={[
                showGroupFilters ? { borderTop: 0 } : { borderTop: 1 },
                {
                    borderBottom:
                        isWebinarOrMeeting && showMoreSettings ? 0 : 1,
                    borderColor: 'divider',
                },
            ]}
            title={translate('offer.subscribeSettings')}
        >
            <TextField
                multiline
                control={control}
                label={translate('offer.descriptionAfterSubscription')}
                name="descriptionAfterSubscription"
            />

            {isWebinarOrMeeting && (
                <Grid container columnSpacing={2}>
                    <Grid
                        size={{
                            sm: 'grow',
                            xs: 12,
                        }}
                    >
                        <TextField
                            control={control}
                            label={translate('offerEventForm.minSubscriptions')}
                            name="minSubscriptions"
                        />
                    </Grid>

                    <Grid
                        size={{
                            sm: 'grow',
                            xs: 12,
                        }}
                    >
                        <TextField
                            control={control}
                            label={translate('maxSubscriptions')}
                            name="maxSubscriptions"
                        />
                    </Grid>
                </Grid>
            )}

            {isWebinarOrMeeting && (
                <Grid container columnSpacing={2}>
                    {isMeeting && (
                        <Grid
                            size={{
                                sm: 'grow',
                                xs: 12,
                            }}
                        >
                            <DateTimePickerField
                                control={control}
                                label={translate('openingDate')}
                                name="openingDate"
                            />
                        </Grid>
                    )}

                    <Grid
                        size={{
                            sm: 'grow',
                            xs: 12,
                        }}
                    >
                        <DateTimePickerField
                            control={control}
                            label={translate('closingDate')}
                            name="closingDate"
                        />
                    </Grid>

                    {isMeeting && (
                        <Grid
                            size={{
                                sm: 'grow',
                                xs: 12,
                            }}
                        >
                            <DateTimePickerField
                                control={control}
                                label={translate('unenrollClosingDate')}
                                name="unenrollClosingDate"
                            />
                        </Grid>
                    )}
                </Grid>
            )}

            <Box sx={{ mt: 1 }}>
                {isWebinarOrMeeting && (
                    <SwitchField
                        control={control}
                        formControlProps={{
                            margin: 'none',
                        }}
                        label={translate('offer.accessTrainerApproval')}
                        name="trainerApproval"
                    />
                )}

                <SwitchField
                    control={control}
                    formControlProps={{
                        margin: 'none',
                    }}
                    label={translate('offer.showParticipantListToParticipants')}
                    name="showParticipantList"
                    onChange={(
                        _e: React.ChangeEvent<HTMLInputElement>,
                        checked: boolean
                    ) => {
                        setValue('showParticipantList', checked, {
                            shouldDirty: true,
                        });

                        // If false the publicParticipantList field must be false too
                        if (!checked && !!publicParticipantList) {
                            setValue('publicParticipantList', checked, {
                                shouldDirty: true,
                            });
                        }
                    }}
                />

                {isWebinarOrMeeting && (
                    <SwitchField
                        control={control}
                        formControlProps={{
                            margin: 'none',
                        }}
                        label={translate('offer.publicParticipantList')}
                        name="publicParticipantList"
                        onChange={(
                            _e: React.ChangeEvent<HTMLInputElement>,
                            checked: boolean
                        ) => {
                            setValue('publicParticipantList', checked, {
                                shouldDirty: true,
                            });

                            // If true the showParticipantList field must be true too
                            if (checked && !showParticipantList) {
                                setValue('showParticipantList', checked);
                            }
                        }}
                    />
                )}

                <SwitchField
                    control={control}
                    formControlProps={{
                        margin: 'none',
                    }}
                    label={translate(
                        'offerEventForm.groupManagersCanEnrollParticipants'
                    )}
                    name="groupManagersCanEnrollParticipants"
                />

                {isWebinarOrMeeting && (
                    <SwitchField
                        control={control}
                        formControlProps={{
                            margin: 'none',
                        }}
                        label={translate('offerEventForm.waitingList')}
                        name="waitingList"
                    />
                )}
            </Box>
        </FormSection>
    );
};
